import React from 'react';
import { toast } from 'sonner';
import { Download, FilePlus2, FileSignature, FileText, Loader2, Pencil, RotateCw } from 'lucide-react';
import RenewDialog from './RenewDialog';
import { expiryLabel, expiryTone } from './ExpiringBanner';
import { Button } from '../../ui/button';
import { adminApi } from '../../../lib/adminAuth';
import StatusPill from './StatusPill';
import ContractEditor from './ContractEditor';
import ProposalEditor from './ProposalEditor';
import { downloadPdf, errMsg, fileName, fmtDate, inr } from './docUtils';

export function useClientAgreements(clientId) {
  const [data, setData] = React.useState(null);
  const [allowed, setAllowed] = React.useState(false);
  const [settings, setSettings] = React.useState(null);
  const reload = React.useCallback(() => {
    if (!clientId) return Promise.resolve();
    return adminApi.get(`/api/admin/contracts/by-client/${clientId}`)
      .then((r) => { setData(r.data); setAllowed(true); })
      .catch(() => { setAllowed(false); setData(null); });
  }, [clientId]);
  React.useEffect(() => { reload(); }, [reload]);
  React.useEffect(() => {
    if (allowed && !settings) adminApi.get('/api/admin/scope-library').then((r) => setSettings(r.data.settings)).catch(() => {});
  }, [allowed, settings]);
  return { data, allowed, settings, reload };
}

const daysTo = (ymd) => Math.round((new Date(`${ymd}T00:00:00`) - new Date(new Date().toLocaleDateString('en-CA') + 'T00:00:00')) / 86400000);

function DocRow({ kind, d, onOpen, onRenew }) {
  const left = kind === 'contract' && d.status === 'signed' && d.end_date && !d.superseded ? daysTo(d.end_date) : null;
  const num = kind === 'contract' ? d.contract_number : d.proposal_number;
  const status = kind === 'contract' ? d.status : d.effective_status;
  const fee = kind === 'contract' ? d.fee : d.monthly_total;
  const base = kind === 'contract' ? '/api/admin/contracts' : '/api/admin/proposals';
  const when = kind === 'contract' ? (d.signed_at || d.sent_at || d.created_at) : (d.accepted_at || d.sent_at || d.created_at);
  return (
    <div className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 bg-white" data-testid={`client-${kind}-row-${d._id}`}>
      {kind === 'contract' ? <FileSignature className="w-4 h-4 text-fuchsia-600 shrink-0" /> : <FileText className="w-4 h-4 text-sky-600 shrink-0" />}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-800 flex items-center gap-2 flex-wrap">{num} <StatusPill status={status} testId={`client-${kind}-status-${d._id}`} />
          {left !== null && left <= 45 && <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${expiryTone(left)}`} data-testid={`client-contract-expiry-${d._id}`}>{expiryLabel(left)}</span>}
          {d.superseded && <span className="text-[10px] font-semibold text-fuchsia-700">renewed</span>}
          {d.changed_since_sent && <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">changed since sent</span>}</p>
        <p className="text-xs text-slate-500">{inr(fee)}/month · {fmtDate(when)}{d.end_date ? ` · ends ${fmtDate(d.end_date)}` : ''}</p>
      </div>
      <button type="button" onClick={() => downloadPdf(`${base}/${d._id}/pdf`, fileName(num))} title={['signed', 'accepted', 'converted'].includes(status) ? 'Download signed PDF' : 'Download PDF'}
        className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-600" data-testid={`client-${kind}-pdf-${d._id}`}><Download className="w-4 h-4" /></button>
      {d.open_renewal && <button type="button" onClick={() => onOpen('contract', d.open_renewal._id)} className="text-xs font-semibold text-fuchsia-700 px-1" data-testid={`client-contract-open-renewal-${d._id}`}>Renewal · {d.open_renewal.status}</button>}
      {kind === 'contract' && d.status === 'signed' && !d.superseded && !d.open_renewal && (
        <button type="button" onClick={() => onRenew(d)} title="Renew" className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-600" data-testid={`client-contract-renew-${d._id}`}><RotateCw className="w-4 h-4" /></button>
      )}
      <button type="button" onClick={() => onOpen(kind, d._id)} title="Open" className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-600" data-testid={`client-${kind}-open-${d._id}`}><Pencil className="w-4 h-4" /></button>
    </div>
  );
}

export function ClientAgreementsSummary({ data, onViewAll }) {
  if (!data) return null;
  const active = data.contracts.find((c) => c._id === data.active_contract_id);
  const latest = active || data.contracts[0];
  const openProposals = data.proposals.filter((p) => ['sent', 'viewed'].includes(p.effective_status)).length;
  return (
    <div className="rounded-xl border border-fuchsia-100 bg-fuchsia-50/40 p-4 flex flex-wrap items-center gap-4" data-testid="client-agreements-summary">
      <FileSignature className="w-5 h-5 text-fuchsia-600" />
      <div className="flex-1 min-w-0 text-sm">
        <p className="font-semibold text-slate-800">Contracts & proposals</p>
        <p className="text-xs text-slate-600" data-testid="client-agreements-summary-text">
          {latest ? <>{latest.contract_number} · {active ? `signed ${fmtDate(active.signed_at)}` : latest.status} · {inr(latest.fee)}/month{active?.end_date ? ` · ends ${fmtDate(active.end_date)}` : ''}</> : 'No contract yet'}
          {` · ${data.contracts.length} contract${data.contracts.length === 1 ? '' : 's'}, ${data.proposals.length} proposal${data.proposals.length === 1 ? '' : 's'}`}
          {openProposals > 0 && ` · ${openProposals} awaiting reply`}
        </p>
      </div>
      <Button size="sm" variant="outline" onClick={onViewAll} data-testid="client-agreements-view-all">View all</Button>
    </div>
  );
}

export default function ClientAgreementsTab({ clientId, data, settings, reload, onClientUpdated }) {
  const [open, setOpen] = React.useState(null);
  const [busy, setBusy] = React.useState('');
  const [renewing, setRenewing] = React.useState(null);
  const close = React.useCallback(() => setOpen(null), []);
  const openContract = React.useCallback((id, send = false) => setOpen({ kind: 'contract', id, autoSend: !!send }), []);
  const create = async (kind) => {
    setBusy(kind);
    try {
      const r = kind === 'contract'
        ? await adminApi.post('/api/admin/contracts', { client_id: clientId })
        : await adminApi.post('/api/admin/proposals', { recipient_type: 'client', client_id: clientId });
      toast.success(`${r.data.contract_number || r.data.proposal_number} created`);
      await reload(); setOpen({ kind, id: r.data._id });
    } catch (e) { toast.error(errMsg(e, 'Could not create')); }
    finally { setBusy(''); }
  };
  if (!data) return <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-fuchsia-600" /></div>;
  const section = (kind, rows, label) => (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-semibold text-slate-800">{label} <span className="text-slate-400 font-normal">({rows.length})</span></p>
        <Button size="sm" variant="outline" disabled={!!busy} onClick={() => create(kind)} data-testid={`client-new-${kind}`}>
          {busy === kind ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FilePlus2 className="w-4 h-4 mr-1" />} New {kind}
        </Button>
      </div>
      {rows.length === 0 ? <p className="text-xs text-slate-500 py-3" data-testid={`client-${kind}-empty`}>No {kind}s yet.</p> : (
        <div className="space-y-2">{rows.map((d) => <DocRow key={d._id} kind={kind} d={d} onOpen={(k, id) => setOpen({ kind: k, id })} onRenew={setRenewing} />)}</div>
      )}
    </div>
  );
  return (
    <div className="space-y-6" data-testid="client-agreements-tab">
      {section('contract', data.contracts, 'Contracts')}
      {section('proposal', data.proposals, 'Proposals')}
      <ContractEditor contractId={open?.kind === 'contract' ? open.id : null} onClose={close} onChanged={reload} settings={settings} onClientUpdated={onClientUpdated}
        autoSend={open?.autoSend} onOpenOther={openContract} />
      {renewing && <RenewDialog contract={renewing} onClose={() => setRenewing(null)} onRenewed={(id, mode) => { setRenewing(null); reload(); openContract(id, mode === 'send'); }} />}
      <ProposalEditor proposalId={open?.kind === 'proposal' ? open.id : null} onClose={close} onChanged={reload} settings={settings} />
    </div>
  );
}
